export type ApiSource = "mock" | "stage" | "prod" | "same-origin";

export const API_ORIGINS: Record<
  Exclude<ApiSource, "mock" | "same-origin">,
  string
> = {
  stage: "https://stage1.husamhelper.ru",
  prod: "https://husamhelper.ru",
};

type ApiSourceOptions = {
  configuredSource?: string;
  disableMsw?: string;
  host?: string;
  nodeEnv?: string;
};

const normalizeSource = (value?: string): ApiSource | null => {
  const normalized = value?.trim().toLowerCase();

  if (normalized === "mock") return "mock";
  if (normalized === "stage" || normalized === "staging") return "stage";
  if (normalized === "prod" || normalized === "production") return "prod";
  return null;
};

export const resolveApiSource = ({
  configuredSource,
  disableMsw,
  host = "",
  nodeEnv: _nodeEnv = "development",
}: ApiSourceOptions): ApiSource => {
  const hostname = (
    host.startsWith("[") ? host.slice(1, host.indexOf("]")) : host.split(":")[0]
  ).toLowerCase();
  if (hostname === "stage1.husamhelper.ru") return "stage";
  if (hostname === "husamhelper.ru") return "prod";

  const isLocal =
    !hostname ||
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1";

  if (isLocal) {
    const explicitSource = normalizeSource(configuredSource);
    if (explicitSource) return explicitSource;
    if (configuredSource) {
      throw new Error(
        `Unknown REACT_APP_API_SOURCE="${configuredSource}". Use mock, stage or prod.`,
      );
    }

    // Backwards compatibility for existing local .env files and CI commands.
    if (disableMsw === "true") return "stage";
    return "mock";
  }

  // Preview and white-label deployments keep using their own backend.
  return "same-origin";
};

const runtimeHost = typeof window === "undefined" ? "" : window.location.host;
const runtimeOrigin =
  typeof window === "undefined" ? "" : window.location.origin;

export const apiSource = resolveApiSource({
  configuredSource: process.env.REACT_APP_API_SOURCE,
  disableMsw: process.env.REACT_APP_DISABLE_MSW,
  host: runtimeHost,
  nodeEnv: process.env.NODE_ENV,
});

export const isMockApiSource = apiSource === "mock";

export const apiOrigin =
  apiSource === "mock"
    ? ""
    : apiSource === "same-origin"
      ? runtimeOrigin
      : API_ORIGINS[apiSource];

// Assets and share links still need a real origin while API calls are mocked.
export const assetOrigin =
  apiSource === "mock"
    ? API_ORIGINS.stage
    : apiSource === "same-origin"
      ? runtimeOrigin
      : API_ORIGINS[apiSource];

export const buildApiUrl = (
  source: ApiSource,
  path: string,
  sameOrigin = "",
): string => {
  if (/^https?:\/\//i.test(path)) return path;

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (source === "mock") return normalizedPath;
  if (source === "same-origin") return `${sameOrigin}${normalizedPath}`;
  return `${API_ORIGINS[source]}${normalizedPath}`;
};

export const buildClassifiedsApiHost = (
  source: ApiSource,
  fallbackOrigin: string,
  configuredHost?: string,
): string => {
  if (source === "mock") return "/api";
  return configuredHost?.replace(/\/$/, "") || `${fallbackOrigin}/api`;
};

export const apiUrl = (path: string): string =>
  buildApiUrl(apiSource, path, runtimeOrigin);
